import * as React from "react";
import { request } from "../../services/cpln";
import { Loader } from "../layout/loader";
import { Link } from "react-router-dom";
import { Checkbox, notification, Tabs } from "antd";
import { Settings, User, Users, Tool, AlertTriangle, Shield } from "react-feather";
import { AccessReport, accessReportHelpers } from "../../schema/types/accessreport";

interface AccessReportPrincipalData {
  permission: string;
  policy: string;
  granted: string;
}

interface PrincipalSectionProps {
  title: string;
  principals: Record<string, AccessReportPrincipalData[]>;
  nameIndex: number;
}

const PrincipalSection: React.FC<PrincipalSectionProps> = ({ title, principals, nameIndex }) => {
  const principalEntries = Object.entries(principals);

  if (principalEntries.length === 0) {
    return null;
  }

  return (
    <div className="mb-6">
      <div className="-mb-4 text-2xl">{title}</div>
      {principalEntries.map(([principal, data]) => (
        <div key={principal}>
          {principal.includes("-allmembers") ? (
            <div className="mt-4">allmembers</div>
          ) : (
            <Link className="inline-block mt-4 ngfocus color-link" to={`/console${principal}`}>
              {principal.split("/")[nameIndex]}
            </Link>
          )}
          <div
            className="flex items-center border py-1 px-4 h-8 table-labels"
            style={{ borderTopLeftRadius: 6, borderTopRightRadius: 6 }}
          >
            <div className="w-4/12">Permission</div>
            <div className="w-4/12">Policy</div>
            <div className="w-4/12">Granted</div>
          </div>
          {data.map((dataItem, dataItemIndex) => {
            const lastItem = dataItemIndex === data.length - 1;
            return (
              <div
                key={`${dataItem.permission}-${dataItem.policy}-${dataItem.granted}`}
                className="flex items-center border border-t-0 py-1 px-4 text-sm"
                style={lastItem ? { borderBottomLeftRadius: 6, borderBottomRightRadius: 6 } : {}}
              >
                <div className="w-4/12">{dataItem.permission}</div>
                <div className="w-4/12">
                  <Link className="inline-block ngfocus color-link" to={`/console${dataItem.policy}`}>
                    {dataItem.policy.split("/")[4]}
                  </Link>
                </div>
                <div className="w-4/12">{dataItem.granted}</div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

interface Props {
  itemLink: string;
}

export const AccessReportPage: React.FC<Props> = ({ itemLink }) => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [report, setReport] = React.useState<AccessReport | null>(null);
  const [hideBuiltin, setHideBuiltin] = React.useState(true);
  const [error, setError] = React.useState("");
  const [hasNoAccess, setHasNoAccess] = React.useState(false);

  React.useEffect(() => {
    request({ url: itemLink + "/-accessreport" })
      .then((res) => {
        setReport(res.data);
        setIsLoading(false);
      })
      .catch((e) => {
        const isPermissionError = e?.response?.status === 403;
        if (isPermissionError) {
          setHasNoAccess(true);
        } else {
          let errorMessage = e?.response?.data?.message;
          if (!errorMessage) errorMessage = e.message;
          setError(errorMessage);
          notification.warning({ message: "Failed", description: errorMessage });
        }
        setIsLoading(false);
      });
  }, [itemLink]);

  // Memoize processed data to avoid repeated calculations
  const byPrincipalData = React.useMemo(() => {
    if (!report) return null;
    return accessReportHelpers.getByPrincipal(report, hideBuiltin);
  }, [report, hideBuiltin]);

  const byPermissionData = React.useMemo(() => {
    if (!report) return [];
    return accessReportHelpers.getByPermission(report, hideBuiltin);
  }, [report, hideBuiltin]);

  const hasCustomByPrincipal = React.useMemo(() => {
    if (!report) return false;
    return accessReportHelpers.getHasCustomByPrincipal(report);
  }, [report]);

  const hasCustomByPermission = React.useMemo(() => {
    if (!report) return false;
    return accessReportHelpers.getHasCustomByPermission(report);
  }, [report]);

  if (isLoading) {
    return <Loader />;
  }

  if (hasNoAccess) {
    return (
      <div className="inline-flex items-center border px-8 py-4">
        <AlertTriangle className="mr-1" />
        <span className="text-lg">You don't have permissions to view this report</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="inline-flex items-center border px-8 py-4">
        <AlertTriangle className="mr-1" />
        <span className="text-lg">Failed to show this information: {error}</span>
      </div>
    );
  }

  if (!report || !byPrincipalData) {
    return null;
  }

  const showNoCustomAccessPrincipal = hideBuiltin && !hasCustomByPrincipal;
  const showNoCustomAccessPermission = hideBuiltin && !hasCustomByPermission;
  const showNoPermissions = !hideBuiltin && byPermissionData.length === 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.625rem" }}>
      <Checkbox onChange={(e) => setHideBuiltin(e.target.checked)} checked={hideBuiltin}>
        Hide Built-in Policies & Principals
      </Checkbox>
      <Tabs
        items={[
          {
            key: "byPrincipal",
            label: "By Principal",
            children: (
              <>
                {showNoCustomAccessPrincipal && (
                  <div className="flex flex-col items-center border p-4">
                    <Shield className="mb-2" />
                    <div className="text-center text-lg">No Custom Access</div>
                  </div>
                )}
                <div>
                  <PrincipalSection title="Users" principals={byPrincipalData.users} nameIndex={4} />
                  <PrincipalSection title="Groups" principals={byPrincipalData.groups} nameIndex={4} />
                  <PrincipalSection title="Service Accounts" principals={byPrincipalData.serviceaccounts} nameIndex={4} />
                  <PrincipalSection title="Identities" principals={byPrincipalData.identities} nameIndex={6} />
                </div>
              </>
            ),
          },
          {
            key: "byPermission",
            label: "By Permission",
            children: (
              <div>
                {showNoCustomAccessPermission && (
                  <div className="flex flex-col items-center border p-4">
                    <Shield className="mb-2" />
                    <div className="text-center text-lg">No Custom Access</div>
                  </div>
                )}
                {showNoPermissions && (
                  <div className="flex flex-col items-center border p-4">
                    <Shield className="mb-2" />
                    <div className="text-center text-lg">No Permissions</div>
                  </div>
                )}
                {byPermissionData.map((permission) => (
                  <div key={permission.name} className="mb-4">
                    <div className="text-2xl">{permission.name}</div>
                    <div>
                      <div
                        className="flex items-center border h-8 p-4 table-labels"
                        style={{ borderTopLeftRadius: 6, borderTopRightRadius: 6 }}
                      >
                        <div className="w-4/12">Principal</div>
                        <div className="w-4/12">Policy</div>
                        <div className="w-4/12">Granted</div>
                      </div>
                      {permission.bindings.map((binding, bindingIndex) => {
                        const lastItem = bindingIndex === permission.bindings.length - 1;
                        const stableKey = `${binding.principalLink}-${binding.grantingPolicyLink}-${bindingIndex}`;

                        return (
                          <div
                            key={stableKey}
                            className="flex items-center border border-t-0 text-sm"
                            style={{
                              padding: "5px 15px",
                              borderBottomLeftRadius: lastItem ? 6 : 0,
                              borderBottomRightRadius: lastItem ? 6 : 0,
                            }}
                          >
                            {binding.principalLink.includes("-allmembers") ? (
                              <div className="w-4/12">allmembers</div>
                            ) : (
                              <div className="w-4/12 flex items-center">
                                {binding.principalLink.length === 7 ? (
                                  <Settings className="mr-2 pb-1" size={16} />
                                ) : binding.principalLink.split("/")[3] === "user" ? (
                                  <User className="mr-2 pb-1" size={16} />
                                ) : binding.principalLink.split("/")[3] === "group" ? (
                                  <Users className="mr-2 pb-1" size={16} />
                                ) : (
                                  <Tool className="mr-2 pb-1" size={16} />
                                )}
                                <Link className="inline-block ngfocus color-link" to={`/console${binding.principalLink}`}>
                                  {binding.principalLink.split("/").length === 7
                                    ? binding.principalLink.split("/")[6]
                                    : binding.principalLink.split("/")[4]}
                                </Link>
                              </div>
                            )}
                            <div className="w-4/12">
                              <Link className="inline-block ngfocus color-link" to={`/console${binding.grantingPolicyLink}`}>
                                {binding.grantingPolicyLink.split("/")[4]}
                              </Link>
                            </div>
                            <div className="w-4/12">{binding.grantedPermissions.join(", ")}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};
